import { motion } from "framer-motion";
import { MoveHorizontal, KeySquare, Ruler } from "lucide-react";

/* Top-down showroom demo: a tightly-packed row of cars, and the Sapeur-360
   platform slides one car out and repositions it into a tight gap. Pure CSS/SVG. */

const Car = ({ color = "#3f3f46", style }) => (
  <div style={style} className="absolute rounded-[6px] shadow-sm"
    // width/height set inline via style
  >
    <div className="w-full h-full rounded-[6px] relative" style={{ background: color }}>
      <div className="absolute left-1/2 -translate-x-1/2 top-[22%] w-[70%] h-[16%] rounded-sm bg-white/25" />
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[20%] w-[70%] h-[18%] rounded-sm bg-white/15" />
    </div>
  </div>
);

const CAR_W = 46;
const CAR_H = 84;

export default function ShowroomDemo() {
  // parked cars (top row) with one empty slot at index 2
  const slots = [0, 1, 3, 4]; // filled slot indexes (2 is the gap)
  const slotX = (i) => 40 + i * (CAR_W + 22);

  return (
    <section data-testid="showroom-demo" className="py-24 lg:py-32 bg-[#0A0A0A] text-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text + stat */}
          <div>
            <p className="font-mono-tech text-sm uppercase tracking-[0.2em] text-[#E63946] font-semibold">
              Showroom & atelier
            </p>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight">
              Des voitures serrées ? Repositionnez-les au millimètre.
            </h2>
            <p className="mt-4 text-lg text-white/60">
              Dans un hall d'exposition, chaque place compte. Sapeur-360 se glisse sous un véhicule
              et l'extrait d'un emplacement étroit pour le replacer exactement où vous voulez —
              <strong className="text-white"> sans le démarrer</strong>, sans manœuvre, sans risque.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { icon: MoveHorizontal, k: "+30 %", v: "de véhicules exposés dans le même espace" },
                { icon: KeySquare, k: "0", v: "démarrage nécessaire" },
                { icon: Ruler, k: "±1 cm", v: "précision de placement" },
              ].map((s) => (
                <div key={s.v} className="border-l-2 border-[#E63946] pl-3">
                  <s.icon className="h-4 w-4 text-[#E63946]" />
                  <div className="font-display text-2xl font-black tracking-tight mt-1.5">{s.k}</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white/40 mt-1 leading-tight">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Animated top-down showroom */}
          <div className="relative rounded-sm border border-white/10 bg-[#111113] overflow-hidden" style={{ height: 320 }}>
            {/* floor grid */}
            <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
              backgroundSize: "28px 28px",
            }} />
            <span className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-wider text-white/40">Hall d'exposition · vue de dessus</span>

            {/* parked cars in the top row */}
            {slots.map((i) => (
              <Car key={i} color="#52525b"
                style={{ left: slotX(i), top: 46, width: CAR_W, height: CAR_H }} />
            ))}
            {/* empty target slot marker (gap at index 2) */}
            <div className="absolute rounded-[6px] border-2 border-dashed border-[#E63946]/60"
              style={{ left: slotX(2), top: 46, width: CAR_W, height: CAR_H }} />

            {/* the moving car + platform */}
            <motion.div
              className="absolute"
              style={{ width: CAR_W, height: CAR_H, left: 40, top: 210 }}
              animate={{
                x: [0, 0, slotX(2) - 40, slotX(2) - 40, slotX(2) - 40, 0],
                y: [0, 0, 0, -164, -164, 0],
              }}
              transition={{ duration: 6, times: [0, 0.12, 0.4, 0.62, 0.85, 1], repeat: Infinity, ease: "easeInOut", repeatDelay: 0.6 }}
            >
              {/* orange platform under the car */}
              <div className="absolute -inset-1 rounded-[8px] bg-[#E9551E]" />
              <div className="absolute -inset-1 rounded-[8px] border border-black/30" />
              {/* the car */}
              <div className="absolute inset-0 rounded-[6px]" style={{ background: "#2563eb" }}>
                <div className="absolute left-1/2 -translate-x-1/2 top-[22%] w-[70%] h-[16%] rounded-sm bg-white/30" />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-[20%] w-[70%] h-[18%] rounded-sm bg-white/20" />
              </div>
            </motion.div>

            <span className="absolute bottom-3 right-3 font-mono-tech text-[10px] uppercase tracking-wider text-[#E9551E]">● Sapeur-360</span>
          </div>
        </div>
      </div>
    </section>
  );
}
